<script setup>
// SiteUp — Personalizar Modo Foco: modo de configuração da tela (todas as
// áreas visíveis), painel com abas, holofote e apply imediato. Ao sair,
// devolve o layout exatamente como estava.
import { ref, computed, nextTick, onBeforeUnmount, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import FocusSpotlight from './FocusSpotlight.vue';
import { useFocusSpotlight } from 'dashboard/composables/useFocusSpotlight';
import {
  FOCUS_MENU_SIDES,
  useFocusMode,
} from 'dashboard/composables/useFocusMode';
import {
  useUISettings,
  DEFAULT_CONVERSATION_SIDEBAR_HIDDEN_ITEMS,
} from 'dashboard/composables/useUISettings';
import { CONVERSATION_SIDEBAR_SECTIONS } from 'dashboard/routes/dashboard/conversation/conversationSidebarSections';
import {
  COLOR_SCHEMES,
  applyColorScheme,
  getColorScheme,
} from 'dashboard/helper/themeHelper';

const TAB_IDS = ['theme', 'header', 'reply', 'contact', 'panel', 'cluster'];
const COMPOSER_SELECTOR = '[data-conversation-composer], .reply-box';

const TARGET_SELECTOR = {
  theme: '',
  header: '[data-testid="focus-spot-header"]',
  reply: COMPOSER_SELECTOR,
  contact: '[data-testid="focus-spot-contact-card"]',
  panel: '[data-testid="focus-spot-sidebar"]',
  cluster: '[data-testid="focus-mode-cluster"]',
};

const GROUPS = {
  header: {
    key: 'conversation_header_hidden_items',
    items: [
      { name: 'sla', labelKey: 'SIDEBAR.FOCUS_MODE.ITEMS.SLA' },
      { name: 'call_button', labelKey: 'SIDEBAR.FOCUS_MODE.ITEMS.CALL' },
    ],
  },
  reply: {
    key: 'conversation_reply_hidden_items',
    items: [
      { name: 'emoji', labelKey: 'SIDEBAR.FOCUS_MODE.ITEMS.EMOJI' },
      { name: 'attach', labelKey: 'SIDEBAR.FOCUS_MODE.ITEMS.ATTACH' },
      { name: 'audio_recorder', labelKey: 'SIDEBAR.FOCUS_MODE.ITEMS.AUDIO' },
      { name: 'signature', labelKey: 'SIDEBAR.FOCUS_MODE.ITEMS.SIGNATURE' },
      { name: 'video_call', labelKey: 'SIDEBAR.FOCUS_MODE.ITEMS.VIDEO' },
      { name: 'insert_article', labelKey: 'SIDEBAR.FOCUS_MODE.ITEMS.ARTICLE' },
    ],
  },
  contact: {
    key: 'conversation_pinned_hidden_items',
    items: [
      {
        name: 'participants',
        labelKey: 'SIDEBAR.FOCUS_MODE.ITEMS.PARTICIPANTS',
      },
      { name: 'team', labelKey: 'SIDEBAR.FOCUS_MODE.ITEMS.TEAM' },
      { name: 'labels', labelKey: 'SIDEBAR.FOCUS_MODE.ITEMS.LABELS' },
    ],
  },
  panel: {
    key: 'conversation_sidebar_hidden_items',
    items: CONVERSATION_SIDEBAR_SECTIONS,
  },
};

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();
const { menuSide, setMenuSide } = useFocusMode();
const startFocusModeTour = inject('startFocusModeTour', () => {});
const {
  spotlightStyle,
  setSelector,
  measure,
  attach: attachSpotlight,
  detach: detachSpotlight,
  scheduleMeasure,
} = useFocusSpotlight();

const isOpen = ref(false);
const confirmingRestore = ref(false);
const activeTab = ref('theme');
const panelRef = ref(null);
const colorScheme = ref(getColorScheme());

let layoutSnapshot = null;
let ignoreOutsideClick = false;

const tabLabel = id => {
  if (id === 'theme') return t('SIDEBAR.FOCUS_MODE.TAB_THEME');
  if (id === 'header') return t('SIDEBAR.FOCUS_MODE.TAB_HEADER');
  if (id === 'reply') return t('SIDEBAR.FOCUS_MODE.TAB_REPLY');
  if (id === 'contact') return t('SIDEBAR.FOCUS_MODE.TAB_CONTACT');
  if (id === 'panel') return t('SIDEBAR.FOCUS_MODE.TAB_PANEL');
  return t('SIDEBAR.FOCUS_MODE.TAB_CLUSTER');
};

const paneTitle = id => {
  if (id === 'theme') return t('SIDEBAR.FOCUS_MODE.GROUP_THEME');
  if (id === 'header') return t('SIDEBAR.FOCUS_MODE.GROUP_HEADER');
  if (id === 'reply') return t('SIDEBAR.FOCUS_MODE.GROUP_REPLY');
  if (id === 'contact') return t('SIDEBAR.FOCUS_MODE.GROUP_CONTACT_CARD');
  if (id === 'panel') return t('SIDEBAR.FOCUS_MODE.GROUP_CONVERSATION_PANEL');
  return t('SIDEBAR.FOCUS_MODE.MENU_SIDE');
};

const paneHint = id => {
  if (id === 'theme') return t('SIDEBAR.FOCUS_MODE.HINT_THEME');
  if (id === 'header') return t('SIDEBAR.FOCUS_MODE.HINT_HEADER');
  if (id === 'reply') return t('SIDEBAR.FOCUS_MODE.HINT_REPLY');
  if (id === 'contact') return t('SIDEBAR.FOCUS_MODE.HINT_CONTACT');
  if (id === 'panel') return t('SIDEBAR.FOCUS_MODE.HINT_PANEL');
  return t('SIDEBAR.FOCUS_MODE.HINT_CLUSTER');
};

const sideLabel = side => {
  if (side === 'auto') return t('SIDEBAR.FOCUS_MODE.MENU_SIDE_AUTO');
  if (side === 'up') return t('SIDEBAR.FOCUS_MODE.MENU_SIDE_UP');
  if (side === 'down') return t('SIDEBAR.FOCUS_MODE.MENU_SIDE_DOWN');
  if (side === 'left') return t('SIDEBAR.FOCUS_MODE.MENU_SIDE_LEFT');
  return t('SIDEBAR.FOCUS_MODE.MENU_SIDE_RIGHT');
};

const themeLabel = scheme => {
  if (scheme === 'light') return t('SIDEBAR.FOCUS_MODE.THEME_LIGHT');
  if (scheme === 'dark') return t('SIDEBAR.FOCUS_MODE.THEME_DARK');
  return t('SIDEBAR.FOCUS_MODE.THEME_AUTO');
};

const setTheme = scheme => {
  colorScheme.value = applyColorScheme(scheme);
};

const activeGroup = computed(() => GROUPS[activeTab.value] || null);
const activeItems = computed(() => activeGroup.value?.items || []);

const hiddenFor = key => {
  const hidden = uiSettings.value?.[key];
  return Array.isArray(hidden) ? hidden : [];
};

const isVisible = (key, name) => !hiddenFor(key).includes(name);

const labelFor = item => {
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.SLA') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.SLA');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.CALL') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.CALL');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.EMOJI') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.EMOJI');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.ATTACH') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.ATTACH');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.AUDIO') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.AUDIO');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.SIGNATURE') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.SIGNATURE');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.VIDEO') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.VIDEO');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.ARTICLE') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.ARTICLE');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.PARTICIPANTS') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.PARTICIPANTS');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.TEAM') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.TEAM');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.LABELS') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.LABELS');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.KANBAN') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.KANBAN');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.WHATSAPP') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.WHATSAPP');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.TASKS') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.TASKS');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.LEAD') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.LEAD');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.LEAD_SCORE') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.LEAD_SCORE');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.SHARED_FILES') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.SHARED_FILES');
  }
  if (item.labelKey === 'JORNADA_360.TITLE') return t('JORNADA_360.TITLE');
  if (item.labelKey === 'CONVERSATION_SIDEBAR.ACCORDION.MACROS') {
    return t('CONVERSATION_SIDEBAR.ACCORDION.MACROS');
  }
  if (item.labelKey === 'CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO') {
    return t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO');
  }
  if (item.labelKey === 'CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES') {
    return t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES');
  }
  if (item.labelKey === 'CONVERSATION_SIDEBAR.ACCORDION.CONTACT_NOTES') {
    return t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_NOTES');
  }
  if (
    item.labelKey === 'CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION'
  ) {
    return t('CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION');
  }
  if (item.labelKey === 'CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE') {
    return t('CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE');
  }
  if (item.labelKey === 'CONVERSATION_SIDEBAR.ACCORDION.LINEAR_ISSUES') {
    return t('CONVERSATION_SIDEBAR.ACCORDION.LINEAR_ISSUES');
  }
  if (item.labelKey === 'CONVERSATION_SIDEBAR.ACCORDION.SHOPIFY_ORDERS') {
    return t('CONVERSATION_SIDEBAR.ACCORDION.SHOPIFY_ORDERS');
  }
  return item.name;
};

const snapshotLayout = () => {
  const settings = uiSettings.value || {};
  layoutSnapshot = {
    is_contact_sidebar_open: settings.is_contact_sidebar_open === true,
    is_copilot_panel_open: settings.is_copilot_panel_open === true,
    workbench_list_mode: settings.workbench_list_mode,
    workbench_list_peek: settings.workbench_list_peek,
  };
};

const applyConfigLayout = () => {
  const settings = uiSettings.value || {};
  const patch = {};
  if (settings.is_contact_sidebar_open !== true) {
    patch.is_contact_sidebar_open = true;
  }
  if (settings.is_copilot_panel_open === true) {
    patch.is_copilot_panel_open = false;
  }
  if (Object.keys(patch).length) updateUISettings(patch);
};

const restoreLayout = () => {
  if (!layoutSnapshot) return;
  const patch = {
    is_contact_sidebar_open: layoutSnapshot.is_contact_sidebar_open,
    is_copilot_panel_open: layoutSnapshot.is_copilot_panel_open,
  };
  if (layoutSnapshot.workbench_list_mode !== undefined) {
    patch.workbench_list_mode = layoutSnapshot.workbench_list_mode;
  }
  if (layoutSnapshot.workbench_list_peek !== undefined) {
    patch.workbench_list_peek = layoutSnapshot.workbench_list_peek;
  }
  updateUISettings(patch);
  layoutSnapshot = null;
};

const measureSpotlight = () => {
  setSelector(TARGET_SELECTOR[activeTab.value]);
  measure();
};

const setVisibility = (key, name, visible) => {
  const next = new Set(hiddenFor(key));
  if (visible) next.delete(name);
  else next.add(name);
  updateUISettings({ [key]: Array.from(next) });
  nextTick(measureSpotlight);
};

let onDocKeydown;
let onDocClick;

function detachObservers() {
  detachSpotlight();
  if (typeof document !== 'undefined') {
    document.removeEventListener('keydown', onDocKeydown);
    document.removeEventListener('click', onDocClick);
  }
}

function attachObservers() {
  detachObservers();
  if (typeof document === 'undefined') return;
  attachSpotlight([
    TARGET_SELECTOR[activeTab.value],
    TARGET_SELECTOR.header,
    COMPOSER_SELECTOR,
    TARGET_SELECTOR.panel,
  ]);
  document.addEventListener('keydown', onDocKeydown);
  document.addEventListener('click', onDocClick);
}

function close() {
  if (!isOpen.value) return;
  isOpen.value = false;
  confirmingRestore.value = false;
  ignoreOutsideClick = false;
  restoreLayout();
  detachObservers();
}

onDocKeydown = event => {
  if (!isOpen.value || event.key !== 'Escape') return;
  event.preventDefault();
  if (confirmingRestore.value) {
    confirmingRestore.value = false;
    return;
  }
  close();
};

onDocClick = event => {
  if (!isOpen.value || ignoreOutsideClick) return;
  if (panelRef.value?.contains(event.target)) return;
  close();
};

const focusActiveTab = () => {
  panelRef.value?.querySelector('[role="tab"][aria-selected="true"]')?.focus();
};

const selectTab = id => {
  if (!TAB_IDS.includes(id)) return;
  activeTab.value = id;
  confirmingRestore.value = false;
  nextTick(() => {
    measureSpotlight();
    attachObservers();
    focusActiveTab();
  });
};

const onTablistKeydown = event => {
  const current = TAB_IDS.indexOf(activeTab.value);
  let next = current;
  if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
    next = (current + 1) % TAB_IDS.length;
  } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
    next = (current - 1 + TAB_IDS.length) % TAB_IDS.length;
  } else if (event.key === 'Home') {
    next = 0;
  } else if (event.key === 'End') {
    next = TAB_IDS.length - 1;
  } else {
    return;
  }
  event.preventDefault();
  selectTab(TAB_IDS[next]);
};

const open = () => {
  snapshotLayout();
  applyConfigLayout();
  isOpen.value = true;
  confirmingRestore.value = false;
  ignoreOutsideClick = true;
  activeTab.value = 'theme';
  colorScheme.value = getColorScheme();
  nextTick(() => {
    measureSpotlight();
    attachObservers();
    focusActiveTab();
    if (typeof window === 'undefined') return;
    window.setTimeout(() => {
      ignoreOutsideClick = false;
      scheduleMeasure();
    }, 80);
  });
};

const replayGuide = () => {
  close();
  nextTick(() => startFocusModeTour());
};

const askRestore = () => {
  confirmingRestore.value = true;
};

const cancelRestore = () => {
  confirmingRestore.value = false;
};

const restoreDefaults = () => {
  updateUISettings({
    conversation_header_hidden_items: [],
    conversation_reply_hidden_items: [],
    conversation_pinned_hidden_items: [],
    conversation_sidebar_hidden_items: [
      ...DEFAULT_CONVERSATION_SIDEBAR_HIDDEN_ITEMS,
    ],
    focus_mode_menu_side: 'auto',
  });
  confirmingRestore.value = false;
  nextTick(measureSpotlight);
};

onBeforeUnmount(() => {
  if (isOpen.value) restoreLayout();
  detachObservers();
});

defineExpose({ open, close });
</script>

<template>
  <TeleportWithDirection to="body">
    <template v-if="isOpen">
      <FocusSpotlight :style-value="spotlightStyle" />

      <div
        ref="panelRef"
        role="dialog"
        aria-modal="true"
        aria-labelledby="focus-mode-settings-title"
        class="fixed left-1/2 top-1/2 z-[80] flex h-[min(29.375rem,calc(100vh-2rem))] w-[min(26.875rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[20px] border border-n-weak bg-n-solid-1 shadow-xl"
        data-testid="focus-mode-settings"
      >
        <div
          role="tablist"
          class="flex w-[150px] shrink-0 flex-col gap-0.5 border-r border-n-weak p-3"
          :aria-label="t('SIDEBAR.FOCUS_MODE.AREAS')"
          @keydown="onTablistKeydown"
        >
          <p
            class="mb-2 px-2 text-[10px] font-extrabold uppercase tracking-[0.13em] text-n-slate-11"
          >
            {{ t('SIDEBAR.FOCUS_MODE.AREAS') }}
          </p>
          <button
            v-for="id in TAB_IDS"
            :key="id"
            type="button"
            role="tab"
            :data-tab="id"
            :aria-selected="activeTab === id"
            :tabindex="activeTab === id ? 0 : -1"
            class="flex min-h-11 w-full items-center gap-2 rounded-[10px] px-2.5 text-left text-xs font-bold transition-colors"
            :class="
              activeTab === id
                ? 'bg-n-alpha-3 text-n-slate-12'
                : 'text-n-slate-11 hover:bg-n-alpha-2'
            "
            :data-testid="`focus-mode-tab-${id}`"
            @click="selectTab(id)"
          >
            <span
              class="size-1.5 shrink-0 rounded-full"
              :class="activeTab === id ? 'bg-n-iris-9' : 'bg-transparent'"
              aria-hidden="true"
            />
            {{ tabLabel(id) }}
          </button>
        </div>

        <div
          class="flex min-w-0 flex-1 flex-col px-4 pb-3 pt-4"
          role="tabpanel"
          aria-labelledby="focus-mode-settings-title"
        >
          <h3
            id="focus-mode-settings-title"
            class="m-0 text-[15px] font-extrabold tracking-tight text-n-slate-12"
          >
            {{ paneTitle(activeTab) }}
          </h3>
          <p class="mb-3 mt-1 text-xs leading-snug text-n-slate-11">
            {{ paneHint(activeTab) }}
          </p>

          <div class="min-h-0 flex-1 overflow-y-auto">
            <div
              v-if="activeTab === 'theme'"
              role="radiogroup"
              class="flex flex-wrap gap-1"
              :aria-label="t('SIDEBAR.FOCUS_MODE.GROUP_THEME')"
            >
              <button
                v-for="scheme in COLOR_SCHEMES"
                :key="scheme"
                type="button"
                role="radio"
                :aria-checked="colorScheme === scheme"
                class="min-h-9 rounded-full border px-3 text-xs font-semibold transition-colors"
                :class="
                  colorScheme === scheme
                    ? 'border-n-iris-9 bg-n-iris-9 text-white'
                    : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
                "
                :data-testid="`focus-mode-theme-${scheme}`"
                @click="setTheme(scheme)"
              >
                {{ themeLabel(scheme) }}
              </button>
            </div>

            <div
              v-if="activeTab === 'cluster'"
              role="radiogroup"
              class="flex flex-wrap gap-1"
              :aria-label="t('SIDEBAR.FOCUS_MODE.MENU_SIDE')"
            >
              <button
                v-for="side in FOCUS_MENU_SIDES"
                :key="side"
                type="button"
                role="radio"
                :aria-checked="menuSide === side"
                class="min-h-9 rounded-full border px-3 text-xs font-semibold transition-colors"
                :class="
                  menuSide === side
                    ? 'border-n-iris-9 bg-n-iris-9 text-white'
                    : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
                "
                :data-testid="`focus-mode-menu-side-${side}`"
                @click="setMenuSide(side)"
              >
                {{ sideLabel(side) }}
              </button>
            </div>

            <div
              v-for="item in activeItems"
              :key="item.name"
              class="flex min-h-11 items-center justify-between gap-3 rounded-lg px-1"
            >
              <span class="text-sm font-medium text-n-slate-12">
                {{ labelFor(item) }}
              </span>
              <ToggleSwitch
                :model-value="isVisible(activeGroup.key, item.name)"
                :data-testid="`focus-mode-toggle-${item.name}`"
                @update:model-value="
                  value => setVisibility(activeGroup.key, item.name, value)
                "
              />
            </div>
          </div>

          <div
            class="mt-2 flex items-center justify-between gap-2 border-t border-n-weak pt-2.5"
          >
            <template v-if="!confirmingRestore">
              <div class="flex min-w-0 flex-col gap-1">
                <button
                  type="button"
                  class="min-h-9 self-start rounded-full px-2 text-left text-xs font-semibold text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                  data-testid="focus-mode-restore"
                  @click="askRestore"
                >
                  {{ t('SIDEBAR.FOCUS_MODE.RESTORE') }}
                </button>
                <button
                  type="button"
                  class="min-h-9 self-start rounded-full px-2 text-left text-xs font-semibold text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                  data-testid="focus-mode-replay-guide"
                  @click="replayGuide"
                >
                  {{ t('SIDEBAR.FOCUS_MODE.REPLAY_GUIDE') }}
                </button>
                <p class="m-0 text-[11px] text-n-slate-11">
                  {{ t('SIDEBAR.FOCUS_MODE.FOCUS_ONLY') }}
                </p>
              </div>
              <button
                type="button"
                class="h-9 shrink-0 rounded-full bg-n-slate-12 px-4 text-sm font-extrabold text-n-background hover:opacity-90"
                data-testid="focus-mode-done"
                @click="close"
              >
                {{ t('SIDEBAR.FOCUS_MODE.DONE') }}
              </button>
            </template>
            <template v-else>
              <p class="m-0 min-w-0 text-xs leading-snug text-n-slate-12">
                {{ t('SIDEBAR.FOCUS_MODE.RESTORE_CONFIRM') }}
              </p>
              <div class="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  class="min-h-9 rounded-full px-3 text-xs font-semibold text-n-slate-11 hover:bg-n-alpha-2"
                  data-testid="focus-mode-restore-cancel"
                  @click="cancelRestore"
                >
                  {{ t('SIDEBAR.FOCUS_MODE.RESTORE_CANCEL') }}
                </button>
                <button
                  type="button"
                  class="min-h-9 rounded-full bg-n-ruby-9 px-3 text-xs font-extrabold text-white hover:bg-n-ruby-10"
                  data-testid="focus-mode-restore-confirm"
                  @click="restoreDefaults"
                >
                  {{ t('SIDEBAR.FOCUS_MODE.RESTORE_YES') }}
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>
  </TeleportWithDirection>
</template>
